/* Created by Interactive 63 - https://interactive63.com */
/**
 * Pointer-driven manipulation of elements on the stage: move, rotate, scale, the transform
 * origin, and keyframe squares on the motion path. Each drag writes through the same auto-key
 * rules as the Inspector and is a single undo step. The coordinate maths lives in geometry.ts.
 */
import { findKeyframe } from '../../../core/timeline/keyframes';
import type { ElementId, PropertyValue, Vec2 } from '../../../core/model/types';
import { setElementOrigin } from '../../store/actions';
import { setPropertyValue, type EditOutcome } from '../../store/property-actions';
import { moveMotionHandle } from '../../store/motion-actions';
import { selectKeyframesWithElements } from '../../store/timeline-actions';
import type { Store } from '../../store/store';
import { toast } from '../../ui/toast';
import {
  advanceRotation,
  angleOf,
  applyMatrix,
  invertMatrix,
  localScaleRatio,
  originFromLocal,
  roundTo,
  screenDeltaToParent,
  snapOriginAxis,
  startRotation,
  type Matrix,
  type Point,
  type ScaleAxes,
} from './geometry';
import { constrainToAxis, snapBox, type ScreenBox } from './snap';

const MOVE_THRESHOLD = 3;
const ROTATE_SNAP_DEGREES = 15;
const ORIGIN_SNAP_RADIUS = 8;
/** Movement before a press on a motion path keyframe becomes a drag rather than a click. */
const MOTION_DRAG_THRESHOLD_PX = 3;

/** What the manipulator needs from the stage that hosts it. */
export interface ManipulationServices {
  store: Store;
  /** The node that is animated for an element: its generated wrapper, or the element itself. */
  node(id: ElementId): SVGGraphicsElement | undefined;
  /** The node's matrix to the screen, including the stage zoom. */
  screenMatrix(node: SVGGraphicsElement): Matrix | null;
  /** Where the element pivots, in client coordinates. */
  pivotScreen(id: ElementId): Point | null;
  /** The value the element shows for a property at the playhead. */
  readValue(id: ElementId, property: 'translate' | 'rotate' | 'scale'): PropertyValue;
  /** The time that edits are written at. */
  time(): number;
  /** Pauses playback and moves the playhead. */
  seek(ms: number): void;
  /** Marks elements as being manipulated, which turns the pivot rulers on for them. */
  setBusy(ids: readonly ElementId[]): void;
  setGuides(guides: { x: number[]; y: number[] } | null): void;
  setReadout(readout: { text: string; x: number; y: number } | null): void;
  /** The union of the elements' on-screen boxes, in client coordinates. */
  selectionBox(ids: readonly ElementId[]): ScreenBox | null;
  /** Boxes to snap a moving selection to: other top-level elements and the artboard. */
  snapTargets(excluding: readonly ElementId[]): ScreenBox[];
  /** Other shapes' centres, for snapping the pivot onto them. */
  shapeCentres(excluding: ElementId): Point[];
}

let dragCounter = 0;

/** A key that is unique to one drag, so successive drags never merge into one undo step. */
function dragKey(kind: string): string {
  dragCounter += 1;
  return `drag:${kind}:${dragCounter}`;
}

/** Whether a property value is a two-component vector. */
function isVec2(value: PropertyValue): value is Vec2 {
  return typeof value === 'object' && 'x' in value && 'y' in value;
}

/**
 * Follows a pointer from a pointer-down event until it is released. Movement is reported only
 * after the pointer has travelled `threshold` pixels, so a plain click never nudges anything.
 */
function trackPointer(
  down: PointerEvent,
  onMove: (event: PointerEvent) => void,
  onEnd: (moved: boolean) => void,
  threshold = 0,
): void {
  let started = false;
  /** Reports pointer movement once the threshold is passed. */
  const move = (event: PointerEvent): void => {
    if (!started) {
      if (Math.hypot(event.clientX - down.clientX, event.clientY - down.clientY) < threshold)
        return;
      started = true;
    }
    onMove(event);
  };
  /** Stops following the pointer. */
  const end = (): void => {
    window.removeEventListener('pointermove', move);
    window.removeEventListener('pointerup', end);
    window.removeEventListener('pointercancel', end);
    onEnd(started);
  };
  window.addEventListener('pointermove', move);
  window.addEventListener('pointerup', end);
  window.addEventListener('pointercancel', end);
}

export class Manipulator {
  private lastRejection = 0;

  /** Creates a manipulator that acts through the given stage services. */
  constructor(private readonly svc: ManipulationServices) {}

  /** Explains once in a while why a drag changed nothing. */
  private report(outcome: EditOutcome): void {
    if (outcome !== 'rejected') return;
    const now = Date.now();
    if (now - this.lastRejection < 2000) return;
    this.lastRejection = now;
    toast.info('Auto-key is off. Click the diamond to add a keyframe.');
  }

  /** Writes a property for several elements, with a per-element value. */
  private write(
    ids: readonly ElementId[],
    property: 'translate' | 'rotate' | 'scale',
    value: (id: ElementId) => PropertyValue,
    key: string,
  ): void {
    this.report(
      setPropertyValue(this.svc.store, ids, property, value, {
        time: this.svc.time(),
        coalesceKey: key,
        onMappedToCycle: () => {
          toast.info('This track repeats, so the change was applied to the first cycle.');
        },
      }),
    );
  }

  /** Moves the elements with the pointer. Shift constrains to an axis, Alt suspends snapping. */
  beginMove(ids: readonly ElementId[], down: PointerEvent): void {
    const { svc } = this;
    const movable = ids.filter((id) => svc.node(id));
    if (movable.length === 0) return;
    const key = dragKey('move');
    const starts = new Map<ElementId, Vec2>();
    const parents = new Map<ElementId, Matrix>();
    for (const id of movable) {
      const value = svc.readValue(id, 'translate');
      starts.set(id, isVec2(value) ? value : { x: 0, y: 0 });
      const parent = svc.node(id)?.parentNode as SVGGraphicsElement | null;
      const matrix = parent ? svc.screenMatrix(parent) : null;
      if (matrix) parents.set(id, matrix);
    }
    const startBox = svc.selectionBox(movable);
    const targets = svc.snapTargets(movable);
    const snapping = svc.store.getState().editor.snapping;

    trackPointer(
      down,
      (event) => {
        let dx = event.clientX - down.clientX;
        let dy = event.clientY - down.clientY;
        if (event.shiftKey) ({ dx, dy } = constrainToAxis(dx, dy));
        if (snapping && !event.altKey && startBox) {
          const snap = snapBox(
            {
              left: startBox.left + dx,
              top: startBox.top + dy,
              right: startBox.right + dx,
              bottom: startBox.bottom + dy,
            },
            targets,
          );
          dx += snap.dx;
          dy += snap.dy;
          svc.setGuides({ x: snap.guidesX, y: snap.guidesY });
        } else {
          svc.setGuides(null);
        }
        svc.setBusy(movable);
        this.write(
          movable,
          'translate',
          (id) => {
            const start = starts.get(id) ?? { x: 0, y: 0 };
            const parent = parents.get(id);
            const delta = parent ? screenDeltaToParent(parent, dx, dy) : { x: dx, y: dy };
            return { x: roundTo(start.x + delta.x, 3), y: roundTo(start.y + delta.y, 3) };
          },
          key,
        );
      },
      (moved) => {
        if (!moved) return;
        svc.setGuides(null);
        svc.setBusy([]);
      },
      MOVE_THRESHOLD,
    );
  }

  /** Rotates the elements about the first one's pivot. Shift snaps to 15 degree steps. */
  beginRotate(ids: readonly ElementId[], down: PointerEvent): void {
    const { svc } = this;
    const primary = ids[0];
    const pivot = primary === undefined ? null : svc.pivotScreen(primary);
    if (primary === undefined || !pivot) return;
    const key = dragKey('rotate');
    const starts = new Map<ElementId, number>();
    for (const id of ids) {
      const value = svc.readValue(id, 'rotate');
      starts.set(id, typeof value === 'number' ? value : 0);
    }
    let state = startRotation(angleOf(pivot, { x: down.clientX, y: down.clientY }));

    trackPointer(
      down,
      (event) => {
        svc.setBusy(ids);
        state = advanceRotation(state, angleOf(pivot, { x: event.clientX, y: event.clientY }));
        const next = (id: ElementId): number => {
          const raw = (starts.get(id) ?? 0) + state.total;
          return roundTo(
            event.shiftKey ? Math.round(raw / ROTATE_SNAP_DEGREES) * ROTATE_SNAP_DEGREES : raw,
            2,
          );
        };
        this.write(ids, 'rotate', next, key);
        svc.setReadout({
          text: `${roundTo(next(primary), 1)}°`,
          x: event.clientX + 14,
          y: event.clientY + 14,
        });
      },
      (moved) => {
        if (!moved) return;
        svc.setReadout(null);
        svc.setBusy([]);
      },
    );
  }

  /**
   * Scales the elements about their pivots, measured along each element's own axes. Shift keeps
   * proportions for handles that control both axes.
   */
  beginScale(ids: readonly ElementId[], axes: ScaleAxes, down: PointerEvent): void {
    const { svc } = this;
    const key = dragKey('scale');
    const starts = new Map<ElementId, { scale: Vec2; matrix: Matrix; pivot: Point }>();
    for (const id of ids) {
      const node = svc.node(id);
      const matrix = node ? svc.screenMatrix(node) : null;
      const pivot = svc.pivotScreen(id);
      const value = svc.readValue(id, 'scale');
      if (matrix && pivot) {
        starts.set(id, { scale: isVec2(value) ? value : { x: 1, y: 1 }, matrix, pivot });
      }
    }
    const scalable = ids.filter((id) => starts.has(id));
    if (scalable.length === 0) return;
    const primary = scalable[0];
    const downPoint = { x: down.clientX, y: down.clientY };

    /** The new scale for one element at the current pointer position. */
    const next = (id: ElementId, pointer: Point, proportional: boolean): Vec2 => {
      const start = starts.get(id);
      if (!start) return { x: 1, y: 1 };
      let ratio = localScaleRatio(start.matrix, start.pivot, downPoint, pointer, axes);
      if (proportional && axes === 'both') {
        const r = Math.abs(ratio.x) >= Math.abs(ratio.y) ? ratio.x : ratio.y;
        ratio = { x: r, y: r };
      }
      return { x: roundTo(start.scale.x * ratio.x, 3), y: roundTo(start.scale.y * ratio.y, 3) };
    };

    trackPointer(
      down,
      (event) => {
        svc.setBusy(scalable);
        const pointer = { x: event.clientX, y: event.clientY };
        this.write(scalable, 'scale', (id) => next(id, pointer, event.shiftKey), key);
        if (primary !== undefined) {
          const value = next(primary, pointer, event.shiftKey);
          svc.setReadout({
            text: `${roundTo(value.x, 2)} × ${roundTo(value.y, 2)}`,
            x: event.clientX + 14,
            y: event.clientY + 14,
          });
        }
      },
      (moved) => {
        if (!moved) return;
        svc.setReadout(null);
        svc.setBusy([]);
      },
    );
  }

  /**
   * Drags an element's pivot. The pointer is converted back into percentages of the element's
   * box. The pivot snaps to the centre of other shapes (so it can be placed on, say, the hole of
   * a flower) and to the nine grid points; hold Shift to turn snapping off.
   */
  beginOrigin(id: ElementId, down: PointerEvent): void {
    const { svc } = this;
    const node = svc.node(id);
    if (!node) return;
    const key = dragKey('origin');
    const centres = svc.shapeCentres(id);

    trackPointer(
      down,
      (event) => {
        svc.setBusy([id]);
        const matrix = svc.screenMatrix(node);
        const inverse = matrix ? invertMatrix(matrix) : null;
        if (!matrix || !inverse) return;
        let point = { x: event.clientX, y: event.clientY };
        let snappedToShape = false;
        if (!event.shiftKey) {
          const near = centres
            .map((c) => ({ c, d: Math.hypot(c.x - point.x, c.y - point.y) }))
            .filter((n) => n.d <= ORIGIN_SNAP_RADIUS)
            .sort((a, b) => a.d - b.d)[0];
          if (near) {
            point = near.c;
            snappedToShape = true;
          }
        }
        const box = node.getBBox();
        let { x, y } = originFromLocal(box, applyMatrix(inverse, point));
        if (!event.shiftKey && !snappedToShape) {
          const scale = Math.hypot(matrix.a, matrix.b) || 1;
          const scaleY = Math.hypot(matrix.c, matrix.d) || 1;
          x = snapOriginAxis(x, (box.width / 100) * scale, ORIGIN_SNAP_RADIUS).value;
          y = snapOriginAxis(y, (box.height / 100) * scaleY, ORIGIN_SNAP_RADIUS).value;
        }
        setElementOrigin(svc.store, id, { x: roundTo(x, 2), y: roundTo(y, 2) }, key);
      },
      (moved) => {
        // A plain click leaves the overlay alone so a double-click can land on the marker.
        if (moved) svc.setBusy([]);
      },
    );
  }

  /** Drags a keyframe square on the motion path, changing that keyframe's position value. */
  beginMotionKey(elementId: ElementId, keyframeId: string, down: PointerEvent): void {
    const { svc } = this;
    const found = findKeyframe(svc.store.getState().project, keyframeId);
    const startValue = found?.keyframe.value;
    if (!startValue || !isVec2(startValue)) return;
    const node = svc.node(elementId);
    const parent = node?.parentNode as SVGGraphicsElement | null | undefined;
    const matrix = parent ? svc.screenMatrix(parent) : null;
    if (!matrix) return;
    const key = dragKey('motion-key');

    trackPointer(
      down,
      (event) => {
        const delta = screenDeltaToParent(
          matrix,
          event.clientX - down.clientX,
          event.clientY - down.clientY,
        );
        const value = {
          x: roundTo(startValue.x + delta.x, 3),
          y: roundTo(startValue.y + delta.y, 3),
        };
        svc.store.updateProject(
          'Move motion keyframe',
          (draft) => {
            const target = findKeyframe(draft, keyframeId);
            if (target) target.keyframe.value = value;
          },
          { coalesceKey: key },
        );
      },
      (moved) => {
        // A click selects the keyframe and brings the playhead to it, as in the timeline.
        if (moved) return;
        selectKeyframesWithElements(svc.store, [keyframeId]);
        svc.seek(found.keyframe.time);
      },
      MOTION_DRAG_THRESHOLD_PX,
    );
  }

  /**
   * Drags one curve handle of a motion path keyframe. The opposite handle stays in line unless
   * the keyframe is a corner or Alt is held, which makes it one.
   */
  beginMotionHandle(
    elementId: ElementId,
    keyframeId: string,
    side: 'in' | 'out',
    down: PointerEvent,
  ): void {
    const { svc } = this;
    const found = findKeyframe(svc.store.getState().project, keyframeId);
    if (!found) return;
    const start = found.keyframe.motion?.[side] ?? { x: 0, y: 0 };
    const node = svc.node(elementId);
    const parent = node?.parentNode as SVGGraphicsElement | null | undefined;
    const matrix = parent ? svc.screenMatrix(parent) : null;
    if (!matrix) return;
    const key = dragKey('motion-handle');

    trackPointer(
      down,
      (event) => {
        const delta = screenDeltaToParent(
          matrix,
          event.clientX - down.clientX,
          event.clientY - down.clientY,
        );
        const handle = { x: roundTo(start.x + delta.x, 3), y: roundTo(start.y + delta.y, 3) };
        moveMotionHandle(svc.store, keyframeId, side, handle, event.altKey || down.altKey, key);
      },
      () => undefined,
    );
  }

  /** Nudges elements by whole units in the parent's space, through the auto-key rules. */
  nudge(ids: readonly ElementId[], dx: number, dy: number): void {
    const starts = new Map<ElementId, Vec2>();
    for (const id of ids) {
      const value = this.svc.readValue(id, 'translate');
      starts.set(id, isVec2(value) ? value : { x: 0, y: 0 });
    }
    this.write(
      ids,
      'translate',
      (id) => {
        const start = starts.get(id) ?? { x: 0, y: 0 };
        return { x: roundTo(start.x + dx, 3), y: roundTo(start.y + dy, 3) };
      },
      `nudge:translate:${ids.join(',')}`,
    );
  }

  /** Nudges an element's pivot by units measured along its own axes. */
  nudgeOrigin(id: ElementId, dx: number, dy: number): void {
    const node = this.svc.node(id);
    const origin = this.svc.store.getState().project.elements[id]?.origin;
    if (!node || !origin) return;
    const box = node.getBBox();
    if (box.width === 0 && box.height === 0) return;
    setElementOrigin(
      this.svc.store,
      id,
      {
        x: roundTo(box.width === 0 ? origin.x : origin.x + (dx / box.width) * 100, 3),
        y: roundTo(box.height === 0 ? origin.y : origin.y + (dy / box.height) * 100, 3),
      },
      `nudge:origin:${id}`,
    );
  }
}
